import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";

const userFormSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    userType: z.enum(["admin", "agent", "client"]),
    clientId: z.string().uuid().nullable(),
    roleIds: z.array(z.string().uuid()).default([]),
    status: z.enum(["active", "inactive", "pending"]).default("pending"),
  })
  .refine((value) => (value.userType === "client" ? Boolean(value.clientId) : true), {
    path: ["clientId"],
    message: "Client users must be linked to a client",
  });

export type UserFormValues = z.infer<typeof userFormSchema>;

interface AvailableRole {
  id: string;
  name: string;
  slug?: string;
  audience: "internal" | "client" | "all";
  isSystem: boolean;
}

interface ClientOption {
  id: string;
  name: string;
}

interface UserFormProps {
  defaultValues?: Partial<UserFormValues>;
  availableRoles: AvailableRole[];
  clients: ClientOption[];
  onSubmit: (data: UserFormValues) => void;
  isPending?: boolean;
}

export function UserForm({
  defaultValues,
  availableRoles,
  clients,
  onSubmit,
  isPending,
}: UserFormProps) {
  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      userType: "agent",
      clientId: null,
      roleIds: [],
      status: "pending",
      ...defaultValues,
    },
  });

  const userType = form.watch("userType");
  const selectedRoleIds = form.watch("roleIds");
  const visibleRoles = useMemo(
    () =>
      availableRoles.filter((role) => {
        if (role.slug === "client-partner") return false;
        return userType === "client"
          ? role.audience !== "internal"
          : role.audience !== "client";
      }),
    [availableRoles, userType],
  );

  const toggleRole = (roleId: string, checked: boolean) => {
    const current = new Set(form.getValues("roleIds"));
    if (checked) current.add(roleId);
    else current.delete(roleId);
    form.setValue("roleIds", [...current], { shouldValidate: true });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="John" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Doe" {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input placeholder="john@example.com" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="userType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>User Type *</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (value !== "client") {
                      form.setValue("clientId", null);
                    }
                  }}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a user type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {userType === "client" && (
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Linked Client *</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value)}
                  defaultValue={field.value ?? undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="roleIds"
          render={() => (
            <FormItem>
              <FormLabel>Assigned Roles</FormLabel>
              <div className="space-y-2 rounded-lg border p-3">
                {visibleRoles.map((role) => (
                  <label
                    key={role.id}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium">{role.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {role.audience}
                        {role.isSystem ? " · System" : ""}
                      </p>
                    </div>
                    <Checkbox
                      checked={selectedRoleIds.includes(role.id)}
                      onCheckedChange={(checked) => toggleRole(role.id, checked === true)}
                    />
                  </label>
                ))}
                {visibleRoles.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No roles are available for this user type.
                  </p>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {defaultValues?.email ? "Update User" : "Create User"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
